import ActionViewConstructor, { ActionView } from "csi/action/ActionView";
import Controller from "csi/action/Controller";
import FormData from "csi/action/FormData";
import PostRequest from "csi/comm/PostRequest";
import config from "csi/config";
import { Context } from "csi/def/Context";
import Tab from "csi/def/Tab";
import { create } from "csi/def/ViewFactory";
import ValuesStore from "csi/store/Values";
import ActionControllerMixin from "csi/wizard/ActionControllerMixin";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";

const CreateLinkedSizeChartsWizard = declare([Controller, ActionControllerMixin], {
	_styleStore: null,
	fdStylePage: null,
	fdSizeChartName: null,
	FIELD_SIZE_CHART_NAME: "SizeChartName",

	/**
	 * determine if Style already has a SC linked to this Shape SC
	 */
	_isPreSelected(ctx: Context, fd: FormData) {
		return !!this._styleStore.get(ctx.getNode().$URL);
	},

	/**
	 * create a store of Styles that have a SC linked to this Shape SC
	 * @override
	 */
	_preAction() {
		this._styleStore = new ValuesStore({
			typeNode: "SizeChart",
			requestParameters: {
				"Qry.Limit.Path": xml.getUrlPath(this.getShapeSizeChartUrl(), xml.path("StyleSizeCharts")),
				"Qry.Value.Path": "Child:__Parent__",
				"Qry.Value.Node": "URL",
			},
		});

		return this._styleStore.query();
	},

	/**
	 * returns the URL of the Shape SC action is being called on
	 */
	getShapeSizeChartUrl() {
		const parentNode = this.getParent();
		if (!xml.isValidURL(this.m_urlNode)) {
			return parentNode.__Parent__;
		} else if (parentNode.$Type === "SizeChart") {
			return this.m_urlParent;
		} else {
			return this.m_urlNode;
		}
	},

	/**
	 * issue request for create style size chart so as to be able to receive return value
	 * @override
	 */
	process() {
		const req = this.m_action.m_request;
		this.setProgress();
		new PostRequest(req.m_module, req.m_operation)
			.addParameter("URL", this.getShapeSizeChartUrl())
			.addParameter("Name", this.fdSizeChartName.getFieldValue(this.FIELD_SIZE_CHART_NAME))
			.addParameter("Styles", this.fdStylePage.getSelectedValue())
			.post({})
			.then(lang.hitch(this, this._onPublishResponse));
	},

	/**
	 * @override
	 * Modify Syle linked view to show only Styles with SizeChart active for type;
	 */
	getWizardViewQuery: function(wizard, view, index) {
		if (index == 0) {
			return new Query()
				.Attr2("TDSMap", xml.OP_EQUALS, {
					Path: "Child:ProductType/Child:ProductionConfig",
					Index:"SizeChart",
					SValue: true,
				})
				.Attr("Active", xml.OP_EQUALS, true);
		} 
		return null;
	},

	getActionView(vr): ActionView {
		const ctx = this.getContext();
		const node = ctx.getNode();
		const parent = ctx.getParent();
		//depends of the context this could be a Size chart or a Shape
		const ctxParentNode = this.getCache()
			.getNode
			(this.getParent().__Parent__);
		let nodeURL;
		let shapeURL;

		if (node?.$Type === "SizeChartRevision") {
			//if the action is executed from the size chart revision context the wizard ref views comming from SC revision 
			// then the NodeURL is sizechart and ctxParentNode.$URL is shape 
			nodeURL = parent.$URL;
			shapeURL = ctxParentNode.$URL;
		} else if (node == null) {
			// ctxParentNode is used when the node is null from the context, it is necesary to get the node from cache and
			// go into the sizechart revision to get the shape as a parent url 
			nodeURL = parent.__Parent__;
			shapeURL = ctxParentNode.__Parent__;
		} else {
			//if the node already has a value and the context is Size chart parent URL are going to be shape and node.url are going to be size chart 
			nodeURL = node.$URL;
			shapeURL = parent.$URL;
		}

		const defView = create(null, "Wizard", "SizeChart");
		defView.tabs = [
			lang.mixin(new Tab(), { m_idView: "ShapeStyles", m_url: shapeURL }),
			lang.mixin(new Tab(), { m_idView: "NameLinkedSizeChartsForm", m_url: nodeURL }),
		];
		return new ActionViewConstructor(this, defView);
	},

	/**
	 * add hook to select and grey out Styles that already have a Size Chart linked to this Shape Size Chart
	 * do nothing if subtype allows for multiple linked sc
	 * @override
	 */
	onNextPage(wizard, currentPage, nextPage, bFinish) {
		const nPage = wizard.getIndexOfPage(nextPage);
		let fd = this.m_formData;
		const bRefresh = false;

		if (nPage === 0) {
			fd = this.fdStylePage = this.fdStylePage || new FormData(this);
			fd.m_bRequireOne = true;
			const shapeSizeChart = this.getContext().cache.getNode(this.getShapeSizeChartUrl());
			if (!config.getSubtypeSetting(shapeSizeChart.Subtype, "AllowMultipleLinkedSCperShapeSC")) {
				fd.m_bOmitSelections = true;
				fd.m_fAggregateSelected = lang.hitch(this, this._isPreSelected);
			}
		} else if (nPage === 1) {
			fd = this.fdSizeChartName = this.fdSizeChartName || new FormData(this);
		}

		return (fd && !bFinish) ? wizard.setFormDataToPage(nextPage, fd, bRefresh) : null;
	},

	/**
	 * @Override
	 * Disable Next button if no selections for style
	 */
	disableNext: function(wizard, page, index) {
		return index == 0 && !(this.fdStylePage.getSelectedNodes().length > 0);
	},
});

export = CreateLinkedSizeChartsWizard;
